import { ParallaxProvider } from 'react-scroll-parallax';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getContestOverviewData } from '@99designs/acquisition-frontend/acquisition/components/ContestOverview/ContestOverviewData';
import ContestOverviewLayout from '@99designs/acquisition-frontend/acquisition/components/ContestOverview/ContestOverviewLayout';
import getListingIdFromUrlSlug from '@99designs/acquisition-frontend/acquisition/components/ContestOverview/getListingIdFromUrlSlug';
import { getCookieByName } from '@99designs/browser';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import DefaultPageLayout from '../../../components/common/DefaultPageLayout';
import { getDefaultPageProps } from '../../../lib/getDefaultPageProps';
import CustomErrorComponent from '../../_error';
import type { SiteConfig } from '@99designs/frontend-components/apps/global-nav/lib/Site';

export default function ContestOverview({
    siteConfig,
    userRole,
    urlSlug,
    contestOverviewResult,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);

    if (contestOverviewResult.statusCode !== 200 && contestOverviewResult.error) {
        return (
            <CustomErrorComponent
                statusCode={contestOverviewResult.statusCode}
            ></CustomErrorComponent>
        );
    }
    return (
        <DefaultPageLayout role={userRole} site={site}>
            <ParallaxProvider>
                <ContestOverviewLayout
                    contest={contestOverviewResult.resp}
                    site={site}
                    currentPath={urlSlug}
                ></ContestOverviewLayout>
            </ParallaxProvider>
        </DefaultPageLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { req, res, query } = context;

    const listingId = getListingIdFromUrlSlug(query.listingId);
    if (listingId === 0 || !req.url) {
        return {
            notFound: true,
        };
    }

    if (process.env.USE_CSRF_CHECK) {
        const csrfToken = getCookieByName('_99csrf', req.headers['cookie'] || '');
        if (!csrfToken) {
            res.statusCode = 401;
            res.end();
            return {
                props: {},
            };
        }
    }

    const [defaultPageProps, contestOverviewResult] = await Promise.all([
        getDefaultPageProps(context),
        getContestOverviewData(req, {
            id: listingId,
            currentUrl: req.url,
        }),
    ]);

    if (contestOverviewResult.statusCode === 404) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            ...defaultPageProps,
            currentUrl: req.url,
            contestOverviewResult,
            urlSlug: `/${query.urlSlug}/contests/${query.listingId}`,
        },
    };
};
